import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { useSetSnapshot } from '@/hooks/use-set-snapshot';
import { SetPresentacionesCard } from '@/components/set-presentaciones-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MonthlyBarChart } from '@/components/monthly-bar-chart';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface MonthRow {
  month: number;
  ingresos: number;
  egresos: number;
  ingresosCount: number;
  egresosCount: number;
  aPagar: number;
  ivaDebito: number;
  ivaCredito: number;
  ivaNeto: number;
}

const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

// Tasa IRP simplificada (estimación; el contador ajusta deducciones reales).
const IRP_RATE = 0.1;

function formatGs(n: number): string {
  return `Gs ${Math.round(Math.abs(n)).toLocaleString('es-PY')}`;
}

function signed(n: number): string {
  return n < 0 ? `-${formatGs(n)}` : formatGs(n);
}

export function AppRentaPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data, isLoading } = useQuery({
    queryKey: ['app-income-expense', year],
    queryFn: () =>
      api<{ year: number; months: MonthRow[]; acumuladoFinal: number }>(
        `/api/app/income-expense?year=${year}`
      ),
    enabled: !!me,
  });

  const { data: setData } = useSetSnapshot();

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Impuesto a la renta: lo decide la SET (obligaciones activas). Fallback al
  // régimen del RUC, y a IRP por defecto.
  const rentaLabel: 'IRP' | 'IRE' =
    setData?.rentaImpuesto ?? (me.rucs[0]?.regimen === 'ire_simple' ? 'IRE' : 'IRP');

  const months = data?.months ?? [];
  const active = months.filter((m) => m.ingresosCount > 0 || m.egresosCount > 0);
  const hasData = active.length > 0;

  const yearIngresos = months.reduce((a, b) => a + b.ingresos, 0);
  const yearEgresos = months.reduce((a, b) => a + b.egresos, 0);
  const baseImponible = data?.acumuladoFinal ?? 0; // ingresos − egresos acumulado
  const irpEstimado = Math.max(0, baseImponible) * IRP_RATE;

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Resumen de Renta</h1>
          <p className="text-sm text-muted-foreground">
            Tu {rentaLabel} estimado del año según ingresos y gastos validados.
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {data && !hasData && (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Info />
              </EmptyMedia>
              <EmptyTitle>Sin movimientos en {year}</EmptyTitle>
              <EmptyDescription>
                Cuando cargues comprobantes de este año, vas a ver acá tu IRP estimado.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      )}

      {data && hasData && (
        <>
          {/* Hero: IRP estimado del año + ingresos/egresos */}
          <Card>
            <CardContent className="grid gap-5 md:grid-cols-[1.4fr_1fr_1fr] md:gap-0">
              <div className="md:pr-6">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {rentaLabel} estimado {year}
                </div>
                <div className="mt-1 text-3xl font-bold tracking-tight text-primary">
                  {formatGs(irpEstimado)}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Sobre base imponible de {signed(baseImponible)}
                </div>
              </div>
              <div className="md:border-l md:border-border md:pl-6">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ArrowUpRight className="size-3.5 text-success" />
                  Ingresos {year}
                </div>
                <div className="mt-1 text-xl font-semibold">{formatGs(yearIngresos)}</div>
              </div>
              <div className="md:border-l md:border-border md:pl-6">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ArrowDownRight className="size-3.5 text-muted-foreground" />
                  Gastos {year}
                </div>
                <div className="mt-1 text-xl font-semibold">{formatGs(yearEgresos)}</div>
              </div>
            </CardContent>
          </Card>

          {/* Chart: ingresos vs egresos mes a mes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ingresos vs gastos — {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <MonthlyBarChart
                data={months.map((m) => ({
                  month: m.month,
                  values: { ingresos: m.ingresos, egresos: m.egresos },
                }))}
                series={[
                  { key: 'ingresos', label: 'Ingresos', colorClass: 'bg-success' },
                  { key: 'egresos', label: 'Gastos', colorClass: 'bg-muted-foreground/40' },
                ]}
                formatValue={formatGs}
                highlightMonth={active[active.length - 1]?.month}
              />
            </CardContent>
          </Card>

          {/* Tabla densa con base acumulada */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mes</TableHead>
                    <TableHead className="text-right">Ingresos</TableHead>
                    <TableHead className="text-right">Gastos</TableHead>
                    <TableHead className="text-right">Resultado</TableHead>
                    <TableHead className="text-right">Base acumulada</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...active].reverse().map((m) => {
                    // Base acumulada hasta ese mes (inclusive).
                    let acumulado = 0;
                    for (let i = 0; i < m.month; i++) acumulado += months[i]?.aPagar ?? 0;
                    return (
                      <TableRow key={m.month}>
                        <TableCell className="font-medium">{MONTH_NAMES[m.month - 1]}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatGs(m.ingresos)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {formatGs(m.egresos)}
                        </TableCell>
                        <TableCell
                          className={
                            'text-right font-medium tabular-nums ' +
                            (m.aPagar < 0 ? 'text-success' : '')
                          }
                        >
                          {signed(m.aPagar)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {signed(acumulado)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="size-3.5" />
            Estimación con tasa simplificada del 10%. Tu contador ajusta deducciones y presenta el
            {' '}{rentaLabel} oficial a la SET.
          </p>
        </>
      )}

      {/* Últimas presentaciones de renta (IRP F.515 / IRE F.501) registradas en SET. */}
      <SetPresentacionesCard formularios={[515, 501]} title="Últimas presentaciones de renta" />
    </AppShell>
  );
}
